import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { lightThemeColors } from "../../assets/styles/colors";
import CustomModal from "../Generic/CustomModal";
import CustomInput from "../Generic/CustomInput";
import Toggle from "../Generic/Toggle";
import { useProjectContext } from "../../context/useProjectContext";
import { useLicensingContext } from "../../context/useLicensingContext";
import { showGenericError, showSuccessNotification, showErrorNotification } from "../../helper/Notifications";
import { useSearchParams } from "react-router-dom";
import { isEmail } from "validator";
import DeleteConfirmationChild from "../Generic/DeleteConfirmationWithChild";
import { REACTIVATE_SUBSCRIPTION, CURRENCY_SYMBOLS } from "../../constants/OptionsConstants";


const CreateSubscriptionModal = ({
  isOpen = true,
  onOpenChange,
  onCreate, // optional callback with payload
  isFirstProject = false, // true if this is the user's first project
  reactivationData = null, // { subscriptionName, planName, currency, emails, yearly }
}) => {
  const { createProject, setSelectedProject, getProjects, addProjectUsers, selectedProject } = useProjectContext();
  const { subscribeToOffer, offers, canAddUsers } = useLicensingContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const isReactivation = !!reactivationData;

  const [name, setName] = useState(reactivationData?.subscriptionName || "");
  const [selectedPlan, setSelectedPlan] = useState("later");
  const [yearly, setYearly] = useState(reactivationData?.yearly || false);
  const [currency, setCurrency] = useState(reactivationData?.currency || "GBP");
  const [emails, setEmails] = useState(reactivationData?.emails || "");
  const [loading, setLoading] = useState(false);
  const [offersPricing, setOffersPricing] = useState(null);
  const [showReactivationConfirm, setShowReactivationConfirm] = useState(false);

  // Fetch offers on mount
  React.useEffect(() => {
    offers(
      (response) => {
        const offersData = response.data?.response || [];

        // Find monthly and yearly B2B Pro offers
        const monthlyOffer = offersData.find(
          (offer) => offer.name?.includes("B2B Pro") && offer.interval === "month"
        );
        const yearlyOffer = offersData.find(
          (offer) => offer.name?.includes("B2B Pro") && offer.interval === "year"
        );

        if (monthlyOffer && yearlyOffer) {
          setOffersPricing({
            monthly: {
              gbp: monthlyOffer.price_per_user_gbp / 100,
              usd: monthlyOffer.price_per_user_usd / 100,
              eur: monthlyOffer.price_per_user_eur / 100,
              name: monthlyOffer.name,
            },
            yearly: {
              gbp: yearlyOffer.price_per_user_gbp / 100,
              usd: yearlyOffer.price_per_user_usd / 100,
              eur: yearlyOffer.price_per_user_eur / 100,
              name: yearlyOffer.name,
            },
          });
        }
      },
      (error) => {
        console.error("Failed to fetch offers:", error);
      }
    );
  }, [offers]);

  const seats = useMemo(() => {
    if (!emails.trim()) return 0;

    const list = Array.from(new Set(
      emails
        .split(",")
        .map((s) => s.trim().toLowerCase())
        .filter((s) => s.length > 0 && isEmail(s))
    ));

    return list.length;
  }, [emails]);

  const pricePerSeatMonthly = useMemo(() => {
    if (offersPricing) {
      const currencyKey = currency.toLowerCase();
      return offersPricing.monthly[currencyKey];
    }
    return 0;
  }, [currency, offersPricing]);

  const pricePerSeatYearly = useMemo(() => {
    if (offersPricing) {
      const currencyKey = currency.toLowerCase();
      return offersPricing.yearly[currencyKey];
    }
    return 0;
  }, [currency, offersPricing]);

  const pricePerSeatDisplay = yearly ? pricePerSeatYearly : pricePerSeatMonthly;
  const periodLabel = yearly ? "year" : "month";
  const currencySymbol = CURRENCY_SYMBOLS[currency];
  const currencyCode = currency;

  const totalPrice = useMemo(() => {
    const seatsCount = seats > 0 ? seats : 1;
    return +(seatsCount * pricePerSeatDisplay).toFixed(2);
  }, [seats, pricePerSeatDisplay]);

  const cycleCurrency = () => {
    setCurrency((prev) => (prev === "GBP" ? "USD" : prev === "USD" ? "EUR" : "GBP"));
  };

  const performReactivation = () => {
    if (!selectedProject) {
      showGenericError();
      console.error("No selected project for reactivation");
      return;
    }

    if (!offersPricing) {
      showGenericError();
      console.error("Offers not loaded yet");
      return;
    }

    setLoading(true);
    const offerName = yearly ? offersPricing.yearly.name : offersPricing.monthly.name;

    subscribeToOffer(
      selectedProject.id,
      offerName,
      currency,
      (response) => {
        setLoading(false);
        if (response.data.url) {
          // Redirect to Stripe checkout
          window.location = response.data.url;
        } else {
          showSuccessNotification("Subscription reactivated successfully");
          onOpenChange?.(false);
          onCreate?.({
            name,
            plan: selectedPlan,
            billingCycle: yearly ? "yearly" : "monthly",
            projectId: selectedProject.id,
          });
        }
      },
      (error) => {
        setLoading(false);
        showGenericError();
        console.log(error);
      }
    );
  };

  const handlePrimary = () => {
    // If reactivation mode, show confirmation modal first
    if (isReactivation) {
      setShowReactivationConfirm(true);
      return;
    }

    // Prepare emails list for adding seats
    let emailList = [];
    if (emails.trim().length > 0) {
      emailList = Array.from(new Set(
        emails
          .split(",")
          .map((s) => s.trim().toLowerCase())
          .filter((s) => s.length > 0 && isEmail(s))
      ));
    }

    setLoading(true);

    // First, create the project without members
    createProject(
      name,
      [], // Create project without API members
      (response) => {
        const project = response.data.project;

        // Helper function to add seats and continue
        const addSeatsAndContinue = (continueCallback) => {
          if (emailList.length > 0) {
            addProjectUsers(
              project.id,
              emailList,
              'meetings',
              continueCallback,
              (error) => {
                setLoading(false);
                showGenericError();
                console.log("Error adding seats:", error);
              }
            );
          } else {
            continueCallback();
          }
        };

        // If pro plan is selected, create subscription with the new project ID
        if (selectedPlan === "pro") {
          // Use offers from state (already fetched on mount)
          if (!offersPricing) {
            setLoading(false);
            showGenericError();
            console.error("Offers not loaded yet");
            return;
          }

          // Get the appropriate offer name based on billing cycle
          const offerName = yearly ? offersPricing.yearly.name : offersPricing.monthly.name;

          // Prepare emails array from the emails input
          const emailList = emails.trim().length > 0
            ? Array.from(new Set(
                emails
                  .split(",")
                  .map((s) => s.trim().toLowerCase())
                  .filter((s) => s.length > 0 && isEmail(s))
              ))
            : [];

          // Helper to continue with subscription flow
          const continueWithSubscription = () => {
            addSeatsAndContinue(() => {
                subscribeToOffer(
                  project.id,
                  offerName,
                  currency,
                  (response) => {
                    setLoading(false);
                    if (response.data.url) {
                      // Store project ID before redirecting to Stripe
                      localStorage.setItem('pendingProjectId', project.id);
                      // Redirect to Stripe checkout
                      window.location = response.data.url;
                    } else {
                      showSuccessNotification("Project and subscription created successfully");
                      setSearchParams((params) => {
                        params.set("project_id", project.id);
                        return params;
                      });
                      setSelectedProject(project);
                      getProjects(
                        () => {
                          onOpenChange?.(false);
                          onCreate?.({
                            name,
                            plan: selectedPlan,
                            billingCycle: yearly ? "yearly" : "monthly",
                            invitedEmails: emails,
                            seats,
                            projectId: project.id,
                          });
                        },
                        (error) => {
                          console.log(error);
                        }
                      );
                    }
                  },
                  (error) => {
                    setLoading(false);
                    showGenericError();
                    console.log(error);
                  }
                );
              });
          };

          // Only check canAddUsers if we have emails to add
          if (emailList.length > 0) {
            canAddUsers(
              emailList, selectedProject?.id,
              (canAddResponse) => {
                // Success response means can_add is true
                continueWithSubscription();
              },
              (error) => {
                // Error response (422) means can_add is false
                setLoading(false);
                // Try different possible paths for the users array
                const users = error?.data?.users || error?.response?.data?.users || [];
                const usersList = users.length > 0 ? ` ${users.join(", ")}` : "";
                showErrorNotification(`Can not add users, they are already enabled in this or other subscriptions.${usersList}`);
              }
            );
          } else {
            // No emails to validate, proceed directly
            continueWithSubscription();
          }
        } else {
          // Just create the project without subscription, but add seats
          addSeatsAndContinue(() => {
            setLoading(false);
            showSuccessNotification("Project created successfully");
            setSearchParams((params) => {
              params.set("project_id", project.id);
              return params;
            });
            setSelectedProject(project);
            getProjects(
              () => {
                onOpenChange?.(false);
                onCreate?.({
                  name,
                  plan: selectedPlan,
                  billingCycle: yearly ? "yearly" : "monthly",
                  invitedEmails: emails,
                  seats,
                  projectId: project.id,
                });
              },
              (error) => {
                console.log(error);
              }
            );
          });
        }
      },
      (error) => {
        setLoading(false);
        showGenericError();
        console.log(error);
      }
    );
  };

  const title = isReactivation
    ? "Reactivate Subscription"
    : isFirstProject
      ? "Create your first subscription"
      : "Create a new subscription";
  const subtitle = isReactivation
    ? "Reactivate your subscription to continue using PRO features."
    : isFirstProject
    ? "Get started by creating your first subscription."
    : "A subscription is your team's shared space to collaborate.";

  const primaryLabel = isReactivation ? "Reactivate Subscription" : "Create";

  // Conditionally create buttons array - exclude Cancel for first project
  const modalButtons = isFirstProject
    ? [
        {
          text: primaryLabel,
          onClick: handlePrimary,
          variant: "primary",
          disabled: name.trim().length < 2 || loading,
        }
      ]
    : [
        {
          text: primaryLabel,
          onClick: handlePrimary,
          variant: "primary",
          disabled: name.trim().length < 2 || loading,
        },
        {
          text: "Cancel",
          onClick: () => onOpenChange?.(false),
          variant: "secondary",
          disabled: loading,
        },
      ];

  return (
    <>
      <DeleteConfirmationChild
        action={performReactivation}
        item={null}
        variant={REACTIVATE_SUBSCRIPTION}
        disableButton={loading}
        open={showReactivationConfirm}
        setOpen={setShowReactivationConfirm}
      />
      <CustomModal
        isOpen={isOpen && !showReactivationConfirm}
        onOpenChange={isFirstProject ? undefined : onOpenChange}
        iconType="subscriptionPlus"
        iconAlignment="left"
        title={title}
        subtitle={subtitle}
        titleAlignment="left"
        autoCloseOnSuccess={false}
        width={620}
        buttonLayout="column"
        betweenButtonsContent={
          selectedPlan === 'pro' && !isReactivation ? (
            <BetweenHelperText>
              (Then {currencySymbol}{totalPrice.toFixed(2)} / {periodLabel}, billed automatically after 7 days)
            </BetweenHelperText>
          ) : null
        }
        buttons={modalButtons}
        disableBackgroundClose={isFirstProject}
      >
        <Content>
        <CustomInput
          label={"Name"}
          name={"subscriptionName"}
          placeholder={"e.g. Internal communication ACME"}
          value={name}
          onChange={(e) => !isReactivation && setName(e.currentTarget.value)}
          readOnly={isReactivation}
          disabled={isReactivation}
          style={{
            cursor: isReactivation ? "not-allowed" : "text",
            backgroundColor: isReactivation ? "#f9fafb" : "#ffffff",
            opacity: isReactivation ? 1 : 1
          }}
        />

        <Divider />

        {/* <SectionGroup>
          <SectionLabel>Plan</SectionLabel>
          <PlanGrid>
            <PlanOption
              data-variant="pro"
              data-selected={selectedPlan === "pro"}
              onClick={() => !isReactivation && setSelectedPlan("pro")}
              style={{ cursor: isReactivation ? "not-allowed" : "pointer", opacity: isReactivation ? 0.8 : 1 }}
            >
              <PlanTop>
                <RadioCircle data-checked={selectedPlan === "pro"} />
                <PlanName>PRO</PlanName>
                <InfoTip onClick={(e) => e.stopPropagation()} tabIndex={0} aria-label="PRO plan details">
                  <InfoIcon aria-hidden />
                  <Tooltip role="tooltip">
                    <TooltipTitle>The PRO plan includes:</TooltipTitle>
                    <ul>
                      <li>Unlimited messages and conversation history</li>
                      <li>AI language translation and interpretation in 126 languages, Video and audio calls</li>
                      <li>Video and audio group calls (up to 10 people)</li>
                      <li>AI voice translation and interpretation in 70 languages</li>
                      <li>Meetings</li>
                      <li>Calendar integration (Outlook, Google Calendar)</li>
                    </ul>
                  </Tooltip>
                </InfoTip>
              </PlanTop>
              <PlanPrice>
                {currencySymbol}{pricePerSeatDisplay.toFixed(2)} <span>Per seat / {periodLabel}.</span>
              </PlanPrice>
            </PlanOption>

            {!isReactivation && (
              <PlanOption
                data-variant="later"
                data-selected={selectedPlan === "later"}
                onClick={() => setSelectedPlan("later")}
              >
                <PlanTop>
                  <RadioCircle data-checked={selectedPlan === "later"} />
                  <LaterLabel>Select a plan later</LaterLabel>
                </PlanTop>
              </PlanOption>
            )}
          </PlanGrid>
        </SectionGroup> */}

        {/* {selectedPlan === "pro" && (
          <>
            <PayRow>
              {!isReactivation && (
                <>
                  <Toggle
                    checked={yearly}
                    onCheckedChange={setYearly}
                    label="Pay yearly"
                  />
                  <DiscountBadge title="Save 10% when billed yearly">10% Discount</DiscountBadge>
                </>
              )}
              <CurrencyTag>
                <CurrencyText>
                  {currencySymbol} <CurrencyCode>{currencyCode}</CurrencyCode>
                </CurrencyText>
                <CurrencyIconButton onClick={cycleCurrency} aria-label="Change currency">
                  <CurrencyIcon />
                </CurrencyIconButton>
              </CurrencyTag>
            </PayRow>

            <SectionGroup>
              <SectionHeader>
                <SectionLabel>{isReactivation ? "Seats" : "Add seats"}</SectionLabel>
                <SeatsBadge>{seats || 0} Seats</SeatsBadge>
              </SectionHeader>
              <SeatsHint>
                {isReactivation
                  ? "These are the current seats associated with this subscription."
                  : "Enter one or more email addresses, separated by commas. Each person will be invited and added to your team. You'll only be billed once they accept."
                }
              </SeatsHint>
              <EmailsTextArea
                placeholder={""}
                value={emails}
                onChange={(e) => !isReactivation && setEmails(e.currentTarget.value)}
                readOnly={isReactivation}
                style={{ cursor: isReactivation ? "not-allowed" : "text", backgroundColor: isReactivation ? "#f9fafb" : "#ffffff" }}
              />
            </SectionGroup>

          </>
        )} */}
      </Content>
    </CustomModal>
    </>
  );
};

export default CreateSubscriptionModal;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SectionGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SectionLabel = styled.p`
  font-size: 14px;
  font-weight: 500;
  color: ${lightThemeColors.darkColor};
  line-height: 20px;
  margin: 0;
`;

const Divider = styled.div`
  height: 1px;
  width: 100%;
  background: #eaecf0;
  margin: 4px 0 0 0;
`;

const PlanGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

const PlanOption = styled.button`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px;
  border-radius: 10px;
  background: #ffffff;
  border: 1px solid #d5d7da; /* gray/300 */
  box-shadow: 0 1px 2px 0 rgba(10, 13, 18, 0.05); /* Shadow/xs */
  cursor: pointer;
  text-align: left;

  &[data-selected="true"] {
    border-color: #7f56d9; /* brand/600 */
    box-shadow: 0 0 0 1px #7f56d9 inset;
  }

  &[data-variant="later"] {
    /* Left-align text, center vertically within the button */
    align-items: flex-start; /* horizontal (cross-axis) */
    justify-content: center; /* vertical (main-axis) */
    text-align: left;
    min-height: 78px;
  }
`;

const PlanTop = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  
  /* Ensure 'later' variant also left-aligns its content */
  ${PlanOption}[data-variant="later"] & {
    justify-content: flex-start;
  }
`;

const RadioCircle = styled.span`
  width: 18px;
  height: 18px;
  border-radius: 9999px;
  border: 2px solid #d0d5dd;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &[data-checked="true"] {
    border-color: #7f56d9;
    box-shadow: inset 0 0 0 4px #7f56d9;
  }
`;

const PlanName = styled.span`
  font-weight: 600; /* Semibold */
  font-size: 20px;
  line-height: 30px;
  color: #414651;
`;

const LaterLabel = styled.span`
  font-weight: 400;
  font-size: 14px;
  line-height: 19px;
  color: #535862;
`;

const PlanPrice = styled.div`
  font-weight: 500; /* Medium */
  font-size: 18px;
  line-height: 24px;
  color: #414651; /* per spec */
  span {
    font-weight: 500; /* Medium */
    font-size: 14px;
    line-height: 14px;
    color: #535862; /* Gray/600 per spec */
    margin-left: 4px;
  }
`;

const InfoTip = styled.span`
  position: relative;
  margin-left: auto;
  display: inline-flex;
  align-items: center;

  &:focus > div,
  &:hover > div {
    opacity: 1;
    transform: translate(-50%, -8px);
    pointer-events: auto;
  }
`;

const Tooltip = styled.div`
  position: absolute;
  left: 50%;
  bottom: 100%;
  transform: translate(-50%, 0);
  background: #0a0d12;
  color: #ffffff;
  border-radius: 10px;
  padding: 12px 14px;
  width: 360px;
  max-width: 80vw;
  box-shadow: 0 8px 24px rgba(10, 13, 18, 0.25);
  opacity: 0;
  transition: opacity 0.15s ease, transform 0.15s ease;
  pointer-events: none;
  z-index: 20;
  font-size: 12px; /* Regular */
  line-height: 16px;

  &::after {
    content: "";
    position: absolute;
    left: 50%;
    bottom: -6px;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-top: 6px solid #0a0d12;
  }

  ul {
    margin: 12px 0 0 0; /* paragraph spacing */
    padding-left: 18px;
    list-style: disc;
  }

  ul > li {
    margin-bottom: 12px;
  }
`;

const TooltipTitle = styled.div`
  font-weight: 400; /* Not bold */
  margin-bottom: 12px; /* paragraph spacing */
  color: #ffffff;
  font-size: 12px;
  line-height: 16px;
`;

const InfoIcon = (props) => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path d="M10 13.3332V9.99984M10 6.6665H10.0084M18.3334 9.99984C18.3334 14.6022 14.6024 18.3332 10 18.3332C5.39765 18.3332 1.66669 14.6022 1.66669 9.99984C1.66669 5.39746 5.39765 1.6665 10 1.6665C14.6024 1.6665 18.3334 5.39746 18.3334 9.99984Z" stroke="#7F56D9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CurrencyIcon = (props) => (
  <svg width="24" height="21" viewBox="0 0 24 21" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path fillRule="evenodd" clipRule="evenodd" d="M2.36881 12.628V16.625C2.36881 19.0409 4.32519 21 6.73769 21H17.223C19.6355 21 21.5919 19.0409 21.5919 16.625V13.1512H19.8443V16.625C19.8443 18.0749 18.6708 19.25 17.223 19.25H6.73769C5.28984 19.25 4.11636 18.0749 4.11636 16.625V12.5974L5.29508 13.7655L6.52448 12.5212L3.23646 9.26275L0 12.537L1.24251 13.7673L2.36881 12.628ZM12.8541 6.125V5.25H11.1066V6.27462C10.0886 6.63512 9.35901 7.60813 9.35901 8.75C9.35901 10.199 10.5334 11.375 11.9803 11.375C12.4627 11.375 12.8541 11.767 12.8541 12.25C12.8541 12.733 12.4627 13.125 11.9803 13.125H10.2328V14.875H11.1066V15.75H12.8541V14.7254C13.8721 14.3649 14.6017 13.3919 14.6017 12.25C14.6017 10.801 13.4273 9.625 11.9803 9.625C11.498 9.625 11.1066 9.233 11.1066 8.75C11.1066 8.267 11.498 7.875 11.9803 7.875H13.7279V6.125H12.8541ZM19.8443 8.39912L18.718 7.25987L17.4755 8.49013L20.712 11.7644L24 8.50588L22.7706 7.26163L21.5919 8.42975V4.375C21.5919 1.95913 19.6355 0 17.223 0H6.73769C4.32519 0 2.36881 1.95913 2.36881 4.375V7.875H4.11636V4.375C4.11636 2.92513 5.28984 1.75 6.73769 1.75H17.223C18.6708 1.75 19.8443 2.92513 19.8443 4.375V8.39912Z" fill="#717680"/>
  </svg>
);

const PayRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const DiscountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center; /* center text horizontally */
  padding: 2px 8px; /* per spec */
  background: #ECFDF3; /* Success/50 */
  color: #027a48; /* Success/700 */
  border-radius: 16px; /* per spec */
  font-size: 12px;
  line-height: 18px; /* per spec */
  font-weight: 500; /* Medium per spec */
  text-align: center;
`;

const CurrencyTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #414651; /* Gray/700 */
  font-size: 14px;
  line-height: 20px;
  font-weight: 400; /* Regular */
  margin-left: auto;
`;

const CurrencyText = styled.span`
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
`;

const CurrencyCode = styled.span`
  font-weight: 500; /* Medium for code */
`;

/* Removed row-under-cards placement; currency is inline with Pay yearly */

const CurrencyIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  color: inherit;
`;

const SeatsBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #f2f4f7; /* gray-100 */
  color: #344054; /* gray-700 */
  border: 1px solid #e4e7ec; /* gray-200 */
  border-radius: 9999px;
  height: 28px;
  padding: 0 10px;
  font-size: 12px;
  font-weight: 600;
`;

const EmailsTextArea = styled.textarea`
  min-height: 100px;
  padding: 10px 12px;
  outline: none;
  width: 100%;
  color: ${lightThemeColors.darkColor};
  border-radius: 8px;
  border: 1px solid #d5d7da; /* gray-300 */
  font-size: 14px;
  line-height: 20px;
  resize: none; /* remove bottom-right resize handle */

  &::placeholder {
    color: #98a2b3;
  }
`;

const BetweenHelperText = styled.p`
  color: #667085;
  font-size: 14px;
  line-height: 20px;
  margin: 0;
  text-align: center;
`;

const SeatsHint = styled.p`
  color: #667085; /* Gray/600 */
  font-size: 14px;
  line-height: 20px;
  margin: 0 0 6px 0;
`;
